import {Link} from "react-router-dom";
import {useEffect, Fragment} from "react";
import {useTypedDispatch, useTypedSelector} from "../app/typedHooks";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faInfoCircle,
  faTimesCircle,
  faQuestionCircle,
  faCheckCircle,
  faCircle,
} from "@fortawesome/pro-solid-svg-icons";
import {Col, Offcanvas, Row, Button, ButtonGroup, Spinner} from "react-bootstrap";
import {updateNotificationPage, requestNotificationDetails} from "../features/notifications/data/notificationSlice";
import {NotificationStatus} from "../features/notifications/data/notificationEnums";
import {formatDistanceToNow} from "date-fns";
import {NotificationDto} from "../types/NotificationDto";
import {NotificationDetailsDto} from "../types/NotificationDetailsDto";

const NotificationsOffCanvas = ({isOpen, handleClose}: {isOpen: boolean; handleClose: () => void}) => {
  const dispatch = useTypedDispatch();

  const isLoaded = useTypedSelector((state) => state.notification.isLoaded);
  const isLoading = useTypedSelector((state) => state.notification.isLoading);
  const isLoadingDetails = useTypedSelector((state) => state.notification.isLoadingDetails);
  const isError = useTypedSelector((state) => state.notification.isError);
  const page = useTypedSelector((state) => state.notification.page);
  const sizePerPage = useTypedSelector((state) => state.notification.sizePerPage);
  const notifications = useTypedSelector((state) => state.notification.notifications);
  const notificationDetails = useTypedSelector((state) => state.notification.notificationDetails);
  const navigation = useTypedSelector((state) => state.navigation.navigation);

  const shownNotifications = notifications.slice((page - 1) * sizePerPage, page * sizePerPage);

  useEffect(() => {
    const missingNotificationIds = shownNotifications.filter(
      (x: NotificationDto) => !notificationDetails.some((y: NotificationDetailsDto) => y.id === x.id)
    );

    if (isLoaded && !isLoadingDetails && !isError && missingNotificationIds.length > 0) {
      dispatch(requestNotificationDetails());
    }
  }, [isLoaded, isError, shownNotifications, notificationDetails, page]);

  const renderNotificationStatus = (status: NotificationStatus) => {
    switch (status) {
      case NotificationStatus.Info:
        return <FontAwesomeIcon icon={faInfoCircle} className="text-primary" />;
      case NotificationStatus.Error:
        return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />;
      case NotificationStatus.Warning:
        return <FontAwesomeIcon icon={faExclamationCircle} className="text-warning" />;
      case NotificationStatus.Success:
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
      case NotificationStatus.Pending:
        return <Spinner size="sm" animation="border" variant="primary" role="status" />;
      default:
        return <FontAwesomeIcon icon={faQuestionCircle} className="text-info" />;
    }
  };

  const renderPageButtons = () => {
    let totalCount = notifications.length;

    if (sizePerPage >= totalCount) {
      return <Fragment />;
    }

    let totalPages = Math.ceil(totalCount / sizePerPage);

    let minPage = page - 2;
    let maxPage = page + 2;

    // Find the minimum page
    while (minPage <= 0) {
      minPage++;
      maxPage++;
    }

    // Find the maximum page
    while (maxPage > totalPages) {
      minPage--;
      maxPage--;
    }

    // Set minimum if we have less than 5 pages
    if (minPage <= 0) {
      minPage = 1;
    }

    let buttonArray = [];

    // Insert back button
    buttonArray.push(
      <Button variant="light" disabled={page === 1} onClick={() => dispatch(updateNotificationPage(page - 1))}>
        &#8826;
      </Button>
    );

    // Generate buttons
    for (let i = minPage; i <= maxPage; i++) {
      let variant = "light";
      if (i === page) {
        variant = "primary";
      }
      buttonArray.push(
        <Button variant={variant} onClick={() => dispatch(updateNotificationPage(i))}>
          {i}
        </Button>
      );
    }

    // Insert forward button
    buttonArray.push(
      <Button
        variant="light"
        disabled={totalPages === 1 || page === totalPages}
        onClick={() => dispatch(updateNotificationPage(page + 1))}
      >
        &#8827;
      </Button>
    );

    return (
      <Fragment>
        <ButtonGroup>{buttonArray}</ButtonGroup>
      </Fragment>
    );
  };

  const renderLink = (notification: NotificationDetailsDto) => {
    const servicePath = window.location.origin + navigation.basePath;
    if (notification.subjectLink.startsWith(servicePath)) {
      return (
        <>
          <Link to={`/${notification.subjectLink.replace(servicePath, "")}`}>{notification.subjectDisplayName}</Link>
        </>
      );
    }

    return (
      <>
        <a href={notification.subjectLink} target="_blank">
          {notification.subjectDisplayName}
        </a>
      </>
    );
  };

  const renderNotifications = () => {
    // Render no notifications
    if (shownNotifications.length === 0) {
      return (
        <Fragment>
          <div className="d-flex justify-content-center mt-3" data-cy="bootstrap-table-loaded">
            <p className="text-center">No notifications were found.</p>
          </div>
        </Fragment>
      );
    }

    let shownNotificationDetails = notificationDetails
      .filter((x: NotificationDetailsDto) => shownNotifications.some((y: NotificationDto) => y.id === x.id))
      .sort(
        (a: NotificationDetailsDto, b: NotificationDetailsDto) =>
          shownNotifications.findIndex((x: NotificationDto) => x.id === a.id) -
          shownNotifications.findIndex((x: NotificationDto) => x.id === b.id)
      );

    // Confirm we have all the notification details
    if (shownNotificationDetails.length < shownNotifications.length) {
      return (
        <Fragment>
          <div className="d-flex justify-content-center mt-3">
            <Spinner animation="border" variant="primary" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Row>
          <Col>
            {shownNotificationDetails.map((notification: NotificationDetailsDto) => {
              const index = shownNotifications.findIndex((x: NotificationDto) => x.id === notification.id);
              const notificationCache = shownNotifications[index];

              return (
                <Row className="notification-event" key={"Notification-" + notification.id}>
                  <Col>
                    <Row className="notification-header">
                      <span>
                        {renderNotificationStatus(notification.status)} <b>{notification.title}</b>
                      </span>
                    </Row>
                    <Row className="notification-body">
                      <span className="notification-anchor">
                        {!notificationCache.isRead && (
                          <FontAwesomeIcon icon={faCircle} className="notification-unread text-primary" />
                        )}
                        {notification.message}
                      </span>
                    </Row>
                    <Row className="text-muted notification-footer">
                      <Col>
                        <span>{formatDistanceToNow(new Date(notification.createdDate), {includeSeconds: true})}</span>
                      </Col>
                      <Col md={8} className="notification-footer-link">
                        <span>
                          View {notification.subjectType} in {renderLink(notification)}
                        </span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
            })}
          </Col>
        </Row>
      </Fragment>
    );
  };

  if (isLoading) {
    return (
      <Offcanvas show={isOpen} onHide={handleClose} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Notifications</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Row>
            <Col>Loading...</Col>
          </Row>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }

  return (
    <Offcanvas show={isOpen} onHide={handleClose} placement="end" className="notification-container">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Notifications</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="notification-content">
        <Row>
          <Col>{renderNotifications()}</Col>
        </Row>
        <Row className="notification-paging">
          <Col xs={12} className="text-center">
            {renderPageButtons()}
          </Col>
        </Row>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default NotificationsOffCanvas;
